import { createInfo } from '@liveauctioneers/hammer-ui-core/card/components/createInfo';
import { SubtleLink } from '@liveauctioneers/hammer-ui-core/link';
import { StarRating } from '@liveauctioneers/hammer-ui-core/starRating';
import { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getItemSummaryHouseReviewCountAndRating } from '@/redux/modules/item/summary/itemSummary.selectors';
import { openReviewsModal } from '@/redux/modules/modal';
import { ItemSummary } from '@/types/item/ItemSummary';
import { ItemCardNavigationTrigger } from '@/utils/amplitude/analytics';
import { useItemCardNavigation } from '@/utils/amplitude/experiments/hooks/useItemCardNavigation';
import { getSellerUrl } from '@/utils/urls';

const MAX_REVIEW_COUNT = 1000;

export const StarRatingComponent = createInfo<ItemSummary & { position: number }>({
    className: 'line-clamp-0',
    useText: ({ itemId, position, promoted }) => {
        const dispatch = useAppDispatch();
        const { onNavigateInitiated } = useItemCardNavigation(itemId);
        const { houseRating, houseReviewCount, sellerId, sellerName } = useAppSelector((state) =>
            getItemSummaryHouseReviewCountAndRating(state, itemId)
        );

        const handleRatingClick = useCallback(() => {
            dispatch(openReviewsModal(sellerId));
        }, [dispatch, sellerId]);

        const handleAuctionHouseClick = useCallback(() => {
            onNavigateInitiated(ItemCardNavigationTrigger.HouseNameClick, { position, promoted });
        }, [onNavigateInitiated, position, promoted]);

        const trailingText = useMemo(() => {
            if (houseReviewCount > MAX_REVIEW_COUNT) {
                const countInThousands = Math.floor(houseReviewCount / 100) / 10;
                return `${countInThousands}k`;
            }
            return houseReviewCount?.toString();
        }, [houseReviewCount]);

        return (
            <div className="grid grid-cols-[1fr_min-content] gap-2xs">
                <SubtleLink
                    className="truncate text-link-secondary"
                    href={getSellerUrl(sellerId, sellerName)}
                    onClick={handleAuctionHouseClick}
                    size="sm"
                >
                    {sellerName}
                </SubtleLink>
                {houseReviewCount > 0 && (
                    <StarRating
                        averageRating={houseRating}
                        compact
                        onClick={handleRatingClick}
                        trailingText={trailingText}
                    />
                )}
            </div>
        );
    },
});
